import { TextWidth } from '@ta-interaktiv/react-disco-grid'
import { ReactNode, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useElementSize, useUpdateEffect } from 'usehooks-ts'

export const AnnotatedImage = ({
  annotations,
  img,
  isMobile,
  colorMode,
  legend,
  maxWidth = 'unset',
}: {
  annotations?: any
  img: string
  isMobile: boolean
  maxWidth?: number | string
  colorMode: 'dark' | 'light'
  legend?: string
}) => {
  //   const container = useRef<HTMLDivElement>(null)
  const [{ width, height }, setSize] = useState({ width: 0, height: 0 })
  const [loaded, setLoaded] = useState(false)
  const [container, size] = useElementSize()

  const LegendEntries = [
    { value: '-0,75 cm', colorLight: '#A16228', colorDark: '#A16228' },
    { value: '-0,75 - -0,25', colorLight: '#D9C0A9', colorDark: '#513114' },
    { value: '-0,25 - 0,25', colorLight: '#F0F0F2', colorDark: '#242424' },
    { value: '0,25 - 0,75', colorLight: '#95D0CF', colorDark: '#16514F' },
    { value: '+0,75 cm', colorLight: '#2CA29F', colorDark: '#2CA29F' },
  ]

  useEffect(() => {
    if (size.width !== undefined && size.height !== undefined) {
      setSize({ width: size.width, height: size.height })
    }
  }, [size])

  function absolute(value: { x: number; y: number }) {
    return { x: (value.x / 100) * width, y: (value.y / 100) * height }
  }

  return (
    <>
      <Legend>
        <div className='legendTitle'>{legend}</div>
        <div className='legendWrapper'>
          {LegendEntries.map((item, index) => (
            <div key={index} className='entry'>
              {index === 0 && (
                <div className='firstEntryValue'>
                  <div>{item.value}</div>
                </div>
              )}
              <div
                className='legendColor'
                style={{
                  backgroundColor:
                    colorMode === 'dark' ? item.colorDark : item.colorLight,
                }}
              ></div>
              {index === LegendEntries.length - 1 && (
                <div className='lastEntryValue'>
                  <div>{item.value}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Legend>
      <>{isMobile && <Spacer style={{ height: '75px' }} />}</>

      <AnnotatedImageRoot ref={container} style={{ maxWidth: maxWidth }}>
        <BGImage
          src={img}
          style={{ maxWidth: maxWidth }}
          onLoad={(e) => {
            let box = (e.target as any).getBoundingClientRect()
            setSize({ width: box.width, height: box.height })
            setLoaded(true)
          }}
        />
        {loaded && (
          <SVGOverlay style={{ maxWidth: maxWidth }}>
            {annotations &&
              annotations.map((anno: any, i: any) => {
                return (
                  <Annotation
                    key={'annotation_' + i}
                    {...{ ...anno, position: absolute(anno.position) }}
                  />
                )
              })}
          </SVGOverlay>
        )}
      </AnnotatedImageRoot>
      <>{isMobile && <Spacer style={{ height: '65px' }} />}</>
    </>
  )
}

const AnnotatedImageRoot = styled.div.attrs({ className: 'AnnotatedImage' })`
  width: 100%;
  /* max-width: 1365px; */
  display: inline-block;
  position: relative;
  /* margin-top: 60px; */
  /* min-height: 300px; */
  @media screen and (max-width: 712px) {
    max-width: 100vw;
  }
`

const SVGOverlay = styled.svg`
  position: absolute;
  overflow: visible;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  /* max-width: 1365px; */
  height: 100%;
  /* min-height: 300px; */
`
const BGImage = styled.img`
  /* max-width: 1365px; */
  width: 100%;
  /* max-height: 50vh; */
  /* pointer-events: none; */
`

export const Annotation = ({
  content,
  position,
  textOffset = { x: 0, y: 0 },
  lineheight = 15,
  linetextOffset = 5,
  showMarker = false,
  showLine = true,
  marker,
  makerStyle = { fill: 'var(--gray-2)', stroke: 'none' },
  lineStyle = { stroke: 'var(--text-color)', strokeWidth: 3 },
}: any) => {
  const annoPos =
    textOffset.x === 0
      ? textOffset.y < 0
        ? 'top'
        : 'bottom'
      : textOffset.x < 0
      ? 'left'
      : 'right'

  return (
    <AnnotationRoot transform={`translate(${position.x},${position.y})`}>
      {showLine && <line x2={textOffset.x} y2={textOffset.y} {...lineStyle} />}
      {showMarker && (marker || <circle r={2} {...makerStyle} />)}
      <AnnotationText
        transform={`translate(${
          textOffset.x +
          (annoPos === 'top' || annoPos === 'bottom'
            ? 0
            : annoPos === 'left'
            ? -linetextOffset
            : linetextOffset)
        },${
          textOffset.y +
          (textOffset.y > 0 ? lineheight : -linetextOffset) +
          (annoPos === 'left' || annoPos === 'right' ? lineheight * 0.6 : 0)
        })`}
        textAnchor={
          annoPos === 'top' || annoPos === 'bottom'
            ? 'middle'
            : annoPos === 'left'
            ? 'end'
            : 'start'
        }
      >
        {content && content}
      </AnnotationText>
    </AnnotationRoot>
  )
}

const AnnotationRoot = styled.g`
  /* min-height: 300px; */
  line-height: 0;
`

const AnnotationText = styled.g``

const Legend = styled.div<{ tenant?: string }>`
  color: var(--text-color);
  font-size: 15px;
  /* margin-top: 1em; */

  .legendTitle {
    margin-bottom: 0.25em;
    line-height: 1.4em;
    text-align: center;
    justify-content: center;
    /* font-weight: 600; */
  }

  .legendWrapper {
    display: flex;
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    gap: 5px 8px;
    /* font-size: 1.5em; */
    margin-bottom: 1em;
    @media screen and (max-width: 712px) {
      margin-bottom: 1.5em;
    }
  }

  .entry {
    display: flex;
    flex-direction: row;
    justify-content: baseline;
    align-items: center;
    white-space: nowrap;
    /* min-width: 82px; */
    font-size: 14px;
    line-height: 2em;
    .firstEntryValue {
      padding-right: 1em;
    }
    .lastEntryValue {
      padding-left: 1em;
    }
    @media screen and (max-width: 712px) {
      font-size: 13px;
    }
  }
  .legendColor {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    @media screen and (max-width: 712px) {
      width: 10px;
      height: 10px;
    }
  }
`
const Spacer = styled.div`
  /* border: 0.5px dotted var(--gray-4); */
  height: 40px;
  @media screen and (max-width: 712px) {
    height: 25px;
  }
  /* margin-bottom: 40px; */
`
